/*---------------React & Libraries-----------------*/
import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

/*---------------Material UI Components-----------*/
import {
  Box,
  Typography,
  Button,
  Divider,
  IconButton,
  Skeleton,
  Paper,
  TextField,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import LocationPinIcon from "@mui/icons-material/LocationPin";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import PaymentsIcon from "@mui/icons-material/Payments";
import FlashOnIcon from "@mui/icons-material/OfflineBolt";

/*---------------Components-----------------------*/
import { CartDetailBreadCrumbItem } from "../../app/data/breadCrumbData";
import BreadcrumbsNav from "../../app/shared/common/Miscellaneous/BreadCrumbNav";
import CouponPopup from "../../app/shared/common/CouponPopup";

/*---------------Stores---------------------------*/
import { store, useStore } from "../../app/stores/store";
import { observer } from "mobx-react-lite";

const CartDetail: React.FC = observer(() => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { cartStore, commonStore, productStore, toasterStore, couponStore } =
    useStore();
  const { customer, fetchCustomerDetail } = commonStore;
  const {
    cartItems,
    getCartItems,
    addTocart,
    getLoadingCart,
    deleteCart,
    validateCartQuantity,
    cartDeliveryTime,
  } = cartStore;

  const cartItemCount = cartItems?.data?.cart_items?.length ?? 0;

  // State
  const [localLoading, setLocalLoading] = useState(true);
  const [couponOpen, setCouponOpen] = useState(false);
  const [validationMessages, setValidationMessages] = useState<
    Record<number, { canAddMore: number; message: string }>
  >({});

  const deliveryType =
    Number(searchParams.get("deliveryType")) || productStore?.deliveryType;
  // Delivery types
  // const isStandardDelivery = deliveryType === 1;
  const isQuickDelivery = deliveryType === 2;

  // Extract URL params
  const outletId = Number(searchParams.get("outletId"));
  const customerTypeId = Number(
    searchParams.get("customerTypeId") || customer?.customerTypeId,
  );
  const warehouseId =
    Number(searchParams.get("warehouseId")) || store?.commonStore?.warehouseId;

  const outletInfo = { outletId, warehouseId, customerTypeId };

  /*---------------Fetch Data Functions-----------------*/
  const fetchCustomer = useCallback(async () => {
    if (!customer?.id) {
      await fetchCustomerDetail(outletInfo.outletId);
    }
  }, [customer?.id, fetchCustomerDetail, outletInfo.outletId]);

  const fetchCart = useCallback(async () => {
    await getCartItems({
      outletId,
      businessTypeId: customerTypeId,
      warehouseId: Number(warehouseId),
      order_type: deliveryType,
    });
  }, [getCartItems, outletId, customerTypeId, warehouseId, deliveryType]);

  const fetchAllCoupons = useCallback(async () => {
    if (!customerTypeId || !outletId) return;
    await couponStore.fetchApplicableCoupons(
      {
        deliveryType: deliveryType,
        warehouseId: Number(warehouseId),
      },
      {
        customerTypeId: customerTypeId,
        outletId: outletId,
      },
    );
  }, [couponStore, deliveryType, warehouseId, customerTypeId, outletId]);

  /*---------------Cart Actions-----------------*/
  const updateQty = async (productId: number, quantity: number) => {
    if (quantity <= 0) return;

    const currentItem = cartItems?.data?.cart_items?.find(
      (item) => item.productId === productId,
    );

    const availableStock =
      currentItem?.productDetails?.product_inventory_stock?.total_stock;
    const maxQuick = currentItem?.productDetails?.max_quantity_quick;
    const maxStandard = currentItem?.productDetails?.max_quantity_standard;

    // Get max quantity based on delivery type (1 = Standard, 2 = Quick)
    const maxAllowed = deliveryType === 2 ? maxQuick : maxStandard;
    const deliveryTypeName = deliveryType === 2 ? "quick" : "standard";

    // Check Available Stock
    if (
      availableStock !== undefined &&
      availableStock !== null &&
      quantity > Number(availableStock)
    ) {
      toasterStore.error("Sorry! We have only limited quantity in stock.");
      return;
    }

    // Check Max Quantity Limit based on delivery type
    if (
      maxAllowed !== undefined &&
      maxAllowed !== null &&
      quantity > Number(maxAllowed)
    ) {
      toasterStore.error(
        `Maximum ${maxAllowed} units available for ${deliveryTypeName} delivery.`,
      );
      return;
    }

    await addTocart({
      productId,
      quantity,
      order_delivery_type: deliveryType,
      clear_cart: false,
      businessTypeId: outletInfo.customerTypeId,
      outletId: outletInfo.outletId,
      warehouseId: Number(outletInfo.warehouseId),
    });
  };

  const handleDeleteItem = async (productId: number) => {
    await addTocart({
      productId,
      quantity: 0,
      order_delivery_type: deliveryType,
      clear_cart: false,
      businessTypeId: outletInfo.customerTypeId,
      outletId: outletInfo.outletId,
      warehouseId: Number(outletInfo.warehouseId),
    });
  };

  const clearCart = async () => {
    try {
      const payload = {
        order_delivery_type: deliveryType,
        warehouseId: Number(outletInfo?.warehouseId),
      };
      const headers = {
        outletId: outletInfo?.outletId,
      };
      await deleteCart(payload, headers);
      await fetchCart();
    } catch (error) {
      console.error("Clear cart error", error);
    }
  };

  /*---------------Effects-----------------*/
  useEffect(() => {
    const init = async () => {
      try {
        await Promise.all([fetchCustomer(), fetchCart()]);
      } catch (error) {
        console.error("Error initializing cart detail page:", error);
      } finally {
        setLocalLoading(false);
      }
    };
    init();
  }, [fetchCustomer, fetchCart]);

  useEffect(() => {
    if (couponOpen) fetchAllCoupons();
  }, [couponOpen, fetchAllCoupons]);

  /*---------------Delivery Time Grouping-----------------*/
  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const formatOptions: Intl.DateTimeFormatOptions = {
      day: "2-digit",
      month: "short",
      year: "numeric",
    };
    return new Intl.DateTimeFormat("en-GB", formatOptions).format(date);
  };

  const { deliveryDaysMap, estimatedDateMap } = useMemo(() => {
    const deliveryDaysMap: Record<number, number> = {};
    const estimatedDateMap: Record<number, string> = {};

    if (cartDeliveryTime && Array.isArray(cartDeliveryTime)) {
      const sortedGroups = [...cartDeliveryTime].sort(
        (a, b) =>
          new Date(a.estimatedDate).getTime() -
          new Date(b.estimatedDate).getTime(),
      );

      sortedGroups.forEach((group) => {
        group.items?.forEach((item: any) => {
          deliveryDaysMap[item.productId] = group.deliveryDays;
          estimatedDateMap[item.productId] = group.estimatedDate;
        });
      });
    }
    return { deliveryDaysMap, estimatedDateMap };
  }, [cartDeliveryTime]);

  const getDeliveryDateText = (
    deliveryTime: number,
    estimatedDate?: string,
  ) => {
    if (estimatedDate) {
      const date = new Date(estimatedDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      date.setHours(0, 0, 0, 0);

      const diffMs = date.getTime() - today.getTime();
      const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

      if (isQuickDelivery) return "Quick Delivery";
      if (diffDays === 0) return "Delivery Today";
      if (diffDays === 1) return "Delivery Tomorrow";

      return `Delivery on ${formatDate(estimatedDate)}`;
    }

    if (deliveryTime <= 1) {
      if (isQuickDelivery) return "Quick Delivery";
      return "Delivery Tomorrow";
    }
    return `Delivery in ${deliveryTime} day${deliveryTime > 1 ? "s" : ""}`;
  };

  // Separate in-stock and out-of-stock items
  const inStockItems =
    cartItems?.data?.cart_items?.filter((item) => item.inStock !== false) || [];
  const outOfStockItems =
    cartItems?.data?.cart_items?.filter((item) => item?.inStock === false) ||
    [];

  // Group in-stock items by delivery time
  const groupedCartItems = inStockItems.reduce(
    (acc, item) => {
      const deliveryDays = store.commonStore.isDirectDelivery ? 0 : (
        deliveryDaysMap[item?.productId] ??
        item?.productDetails?.deliveryTime ??
        0
      );
      if (!acc[deliveryDays]) acc[deliveryDays] = [];
      acc[deliveryDays].push(item);
      return acc;
    },
    {} as Record<number, any[]>,
  );

  const sortedDeliveryTimes = Object.keys(groupedCartItems)
    .map(Number)
    .sort((a, b) => a - b);

  /*---------------Checkout-----------------*/
  const handleCheckout = async () => {
    if (inStockItems?.length === 0) return;

    // Prevent checkout if no delivery types available
    if (
      !store.commonStore.actualAvailableDeliveryTypes ||
      store.commonStore.actualAvailableDeliveryTypes.length === 0
    ) {
      toasterStore.error("No delivery options available for this outlet");
      return;
    }

    if (
      !outletInfo.outletId ||
      !outletInfo.warehouseId ||
      !outletInfo.customerTypeId ||
      !customer?.customerOrgId
    ) {
      toasterStore.error("Missing outlet information to validate cart.");
      return;
    }

    const validationResponse = await validateCartQuantity(
      {
        order_delivery_type: deliveryType,
        warehouseId: Number(outletInfo.warehouseId),
      },
      {
        outletId: outletInfo.outletId,
        customerTypeId: Number(outletInfo.customerTypeId),
        customerOrgId: Number(customer.customerOrgId),
      },
    );

    const nextValidation: Record<
      number,
      { canAddMore: number; message: string }
    > = {};
    (validationResponse?.data ?? []).forEach((item) => {
      nextValidation[item.productId] = {
        canAddMore: item.canAddMore,
        message: item.message,
      };
    });
    setValidationMessages(nextValidation);

    const violations = (validationResponse?.data ?? []).filter(
      (v) => v.canAddMore === 0 || v.requestedQuantity > v.canAddMore,
    );

    if (violations.length > 0) {
      toasterStore.error(
        "Some products exceed the allowed quantity. Please review the highlighted items.",
      );
      return;
    }

    // Clear previous validation messages on success
    setValidationMessages({});

    // Navigate to checkout with query parameters (data will be fetched from API in CheckoutPage)
    const params = new URLSearchParams();
    params.append("outletId", String(outletInfo.outletId));
    params.append("customerTypeId", String(customerTypeId));
    params.append("warehouseId", String(outletInfo.warehouseId));
    params.append("deliveryType", String(deliveryType));

    navigate(`/checkout?${params.toString()}`);
  };

  const totals = cartItems?.data;
  const canCheckout = true;
  const loader = localLoading || getLoadingCart();

  /*---------------UI Components-----------------*/
  const CartItemSkeleton = () => (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      p={2}
      mb={1.5}
      bgcolor="#fff"
      borderRadius={1}
    >
      <Skeleton variant="rectangular" width={50} height={50} />
      <Skeleton variant="text" width={100} sx={{ ml: 2 }} />
      <Skeleton variant="text" width={40} />
      <Skeleton variant="text" width={40} />
    </Box>
  );

  const CartItemRow: React.FC<{ item: any }> = ({ item }) => {
    const product = item?.productDetails;
    const variant = item?.product_varient;
    const image = product?.product_images?.[0]?.path || "";

    const [inputValue, setInputValue] = useState<string>(
      String(item.quantity ?? ""),
    );
    const [showInput, setShowInput] = useState<boolean>(
      (item.quantity ?? 0) > 0,
    );
    const [localLoadingItem, setLocalLoadingItem] = useState(false);

    useEffect(() => {
      setInputValue(String(item.quantity ?? ""));
      setShowInput((item.quantity ?? 0) > 0);
    }, [item.quantity]);

    const availableStock =
      item?.productDetails?.product_inventory_stock?.total_stock;
    const maxQuick = item?.productDetails?.max_quantity_quick;
    const maxStandard = item?.productDetails?.max_quantity_standard;

    // Get max quantity based on delivery type (1 = Standard, 2 = Quick)
    const maxAllowed = deliveryType === 2 ? maxQuick : maxStandard;
    const deliveryTypeName = deliveryType === 2 ? "quick" : "standard";

    const onChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;

      if (v === "") {
        setInputValue("");
        return;
      }

      if (!/^\d+$/.test(v)) return;

      const numericValue = Number(v);

      // Check stock limit
      if (
        availableStock !== undefined &&
        availableStock !== null &&
        numericValue > Number(availableStock)
      ) {
        toasterStore.error("Sorry! We have only limited quantity in stock.");
        setInputValue(String(item.quantity));
        return;
      }

      // Check delivery-type-specific max quantity
      if (
        maxAllowed !== undefined &&
        maxAllowed !== null &&
        numericValue > Number(maxAllowed)
      ) {
        toasterStore.error(
          `Maximum ${maxAllowed} units available for ${deliveryTypeName} delivery.`,
        );
        setInputValue(String(item.quantity));
        return;
      }

      setInputValue(v);
    };

    const submitQty = async (newQtyNum: number) => {
      if (newQtyNum === 0) {
        setLocalLoadingItem(true);
        try {
          await handleDeleteItem(item.productId);
        } finally {
          setLocalLoadingItem(false);
        }
        return;
      }

      if (
        availableStock !== undefined &&
        availableStock !== null &&
        newQtyNum > Number(availableStock)
      ) {
        toasterStore.error("Sorry! We have only limited quantity in stock.");
        setInputValue(String(item.quantity));
        return;
      }

      if (
        maxAllowed !== undefined &&
        maxAllowed !== null &&
        newQtyNum > Number(maxAllowed)
      ) {
        toasterStore.error(
          `Maximum ${maxAllowed} units available for ${deliveryTypeName} delivery.`,
        );
        setInputValue(String(item.quantity));
        return;
      }

      setLocalLoadingItem(true);
      try {
        await updateQty(item.productId, newQtyNum);
      } finally {
        setLocalLoadingItem(false);
      }
    };

    const handleBlur = async () => {
      const newQty = parseInt(inputValue) || 0;
      if (newQty === item.quantity || inputValue === "") {
        setInputValue(String(item.quantity));
        return;
      }
      await submitQty(newQty);
    };

    const handleQuantityChange = async (delta: number) => {
      const newQty = (item.quantity ?? 0) + delta;
      if (newQty <= 0) {
        setLocalLoadingItem(true);
        try {
          await handleDeleteItem(item.productId);
        } finally {
          setLocalLoadingItem(false);
        }
        return;
      }
      setLocalLoadingItem(true);
      try {
        await updateQty(item.productId, newQty);
      } finally {
        setLocalLoadingItem(false);
      }
    };

    const handleAddClick = async () => {
      const initialQty = variant?.varient_denomination_counter || 1;
      setLocalLoadingItem(true);
      try {
        await updateQty(item.productId, initialQty);
        setShowInput(true);
        setInputValue(String(initialQty));
      } finally {
        setLocalLoadingItem(false);
      }
    };

    const isOutOfStock = item.inStock === false;
    const validationInfo = validationMessages[item.productId];
    const shouldShowValidation =
      validationInfo &&
      (validationInfo.canAddMore === 0 ||
        (item.quantity ?? 0) > Number(validationInfo.canAddMore));
    const validationText = !validationInfo
      ? ""
      : validationInfo.canAddMore === 0
        ? "Maximum order quantity exceeded. Please remove this item to continue."
        : `Maximum ${validationInfo.canAddMore} quantity can be added for this product.`;
    const isLoading = loader || localLoadingItem;

    return (
      <Box
        key={item?.id}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        p={2}
        mb={1.5}
        bgcolor="#fff"
        borderRadius={1}
        sx={{
          border: "1px solid #f0f0f0",
          opacity: isOutOfStock ? 0.6 : 1,
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 1.5, md: 0 },
        }}
      >
        {/* Product Info */}
        <Box
          display="flex"
          alignItems="center"
          gap={2}
          flex={4}
          sx={{ width: { xs: "100%", md: "auto" } }}
        >
          <Box
            width={50}
            height={50}
            borderRadius={1}
            overflow="hidden"
            flexShrink={0}
            bgcolor="#f4f4f4"
          >
            <img
              src={image}
              alt={product.name}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </Box>
          <Box flex={1}>
            <Typography fontWeight={600} fontSize="0.85rem">
              {product.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ₹{variant.varient_denomination_of_uom}
            </Typography>
            {isOutOfStock && (
              <Typography variant="caption" color="error" fontWeight={600}>
                Currently Unavailable
              </Typography>
            )}
          </Box>
        </Box>

        {/* Mobile: Quantity Controls and Amount */}
        <Box
          sx={{
            display: { xs: "flex", md: "none" },
            width: "100%",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            {!isOutOfStock ? (
              showInput && item.quantity > 0 ? (
                <>
                  <Button
                    variant="contained"
                    color="success"
                    sx={{
                      minWidth: 28,
                      height: 28,
                      padding: "4px",
                      fontSize: "0.875rem",
                    }}
                    disabled={isLoading}
                    onClick={() => handleQuantityChange(-1)}
                  >
                    −
                  </Button>
                  <TextField
                    value={inputValue}
                    onChange={onChangeInput}
                    onBlur={handleBlur}
                    size="small"
                    disabled={isLoading}
                    sx={{
                      width: "50px",
                      "& .MuiOutlinedInput-root": {
                        height: "32px",
                        "& input": { textAlign: "center", padding: "6px 8px" },
                      },
                    }}
                  />
                  <Button
                    variant="contained"
                    color="success"
                    sx={{
                      minWidth: 28,
                      height: 28,
                      padding: "4px",
                      fontSize: "0.875rem",
                    }}
                    disabled={isLoading}
                    onClick={() => handleQuantityChange(1)}
                  >
                    +
                  </Button>
                </>
              ) : (
                <Button
                  variant="contained"
                  color="success"
                  sx={{
                    minWidth: 28,
                    height: 28,
                    padding: "4px",
                    fontSize: "0.875rem",
                  }}
                  disabled={isLoading}
                  onClick={handleAddClick}
                >
                  Add
                </Button>
              )
            ) : (
              <Typography variant="body2" color="text.secondary">
                Out of Stock
              </Typography>
            )}
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <Typography fontWeight={600} sx={{ fontSize: "0.9rem" }}>
              ₹{(item?.item_price * item?.quantity).toFixed(2)}
            </Typography>
            <IconButton
              onClick={() => handleDeleteItem(item?.productId)}
              disabled={isLoading}
              size="small"
              sx={{ padding: "4px" }}
            >
              <DeleteIcon sx={{ color: "#F44336", fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>

        {/* Desktop: Quantity Controls */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          gap={1}
          flex={2}
          sx={{ display: { xs: "none", md: "flex" } }}
        >
          {!isOutOfStock ? (
            showInput && item.quantity > 0 ? (
              <>
                <Button
                  variant="contained"
                  color="success"
                  sx={{ minWidth: 32, height: 32 }}
                  disabled={isLoading}
                  onClick={() => handleQuantityChange(-1)}
                >
                  −
                </Button>
                <TextField
                  value={inputValue}
                  onChange={onChangeInput}
                  onBlur={handleBlur}
                  disabled={isLoading}
                  size="small"
                  sx={{
                    width: "50px",
                    "& .MuiOutlinedInput-root": {
                      height: "32px",
                      "& input": { textAlign: "center", padding: "6px 8px" },
                    },
                  }}
                />
                <Button
                  variant="contained"
                  color="success"
                  sx={{ minWidth: 32, height: 32 }}
                  disabled={isLoading}
                  onClick={() => handleQuantityChange(1)}
                >
                  +
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                color="success"
                sx={{ minWidth: 32, height: 32 }}
                disabled={isLoading}
                onClick={handleAddClick}
              >
                Add
              </Button>
            )
          ) : (
            <Typography variant="body2" color="text.secondary">
              Out of Stock
            </Typography>
          )}
        </Box>

        {/* Desktop: Amount & Delete */}
        <Box
          flex={1}
          textAlign="right"
          sx={{ display: { xs: "none", md: "block" } }}
        >
          <Typography fontWeight={600}>
            ₹{(item?.item_price * item?.quantity).toFixed(2)}
          </Typography>
        </Box>
        <Box
          width={48}
          textAlign="right"
          sx={{ display: { xs: "none", md: "block" } }}
        >
          <IconButton
            onClick={() => handleDeleteItem(item?.productId)}
            disabled={loader}
          >
            <DeleteIcon sx={{ color: "#F44336" }} />
          </IconButton>
        </Box>
        {shouldShowValidation && (
          <Box width="100%" mt={1}>
            <Typography variant="caption" color="error" fontWeight={600}>
              {validationText}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box
      display="flex"
      flexDirection={{ xs: "column", md: "row" }}
      p={{ xs: 0, md: 3 }}
      gap={3}
    >
      {/* LEFT SIDE – Cart Items */}
      <Box flex={{ xs: 1, md: 7 }} width={{ xs: "100%", md: "auto" }}>
        {/* Breadcrumbs and Clear Button */}
        <Box
          mb={2}
          sx={{
            display: { xs: "block", md: "flex" },
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ mb: { xs: 1, md: 0 } }}>
            <BreadcrumbsNav
              items={CartDetailBreadCrumbItem(
                String(customer?.id ?? ""),
                customer?.name,
              )}
            />
          </Box>

          <Box
            display="flex"
            justifyContent={{ xs: "flex-end", md: "flex-start" }}
          >
            <Button
              onClick={clearCart}
              color="error"
              disabled={loader || cartItems?.data?.cart_items.length === 0}
              size="medium"
              startIcon={<DeleteIcon sx={{ color: "#F44336" }} />}
            >
              Clear All
            </Button>
          </Box>
        </Box>

        {/* Table Headers - Desktop only */}
        <Box
          display="flex"
          px={1}
          py={1.5}
          bgcolor="#fff"
          sx={{ display: { xs: "none", md: "flex" } }}
        >
          <Box flex={4}>
            <Typography fontWeight={600}>
              Products ({cartItems?.data?.cart_items.length ?? 0})
            </Typography>
          </Box>
          <Box flex={2} textAlign="center">
            <Typography fontWeight={600}>Quantity</Typography>
          </Box>
          <Box flex={1} textAlign="right">
            <Typography fontWeight={600}>Amount</Typography>
          </Box>
          <Box width={48} />
        </Box>

        {/* Mobile: Product Count */}
        <Box sx={{ display: { xs: "block", md: "none" }, mb: 1, px: 1 }}>
          <Typography fontWeight={600} variant="body2">
            Products ({cartItems?.data?.cart_items.length ?? 0})
          </Typography>
        </Box>

        {/* Cart Items List */}
        <Box
          sx={{
            maxHeight: { xs: "none", md: "70vh" },
            overflowY: { xs: "visible", md: "auto" },
            pr: { xs: 0, md: 1 },
          }}
        >
          {loader ? (
            Array.from({ length: 3 }).map((_, i) => (
              <CartItemSkeleton key={i} />
            ))
          ) : !cartItems?.data?.cart_items.length ? (
            <Typography align="center" sx={{ mt: 4 }}>
              Your cart is empty.
            </Typography>
          ) : (
            <>
              {/* Out of Stock Items Section */}
              {outOfStockItems.length > 0 && (
                <Paper
                  elevation={0}
                  sx={{
                    mb: 2,
                    p: 2,
                    bgcolor: "#fff8f8",
                    border: "1px solid #ffebee",
                    borderRadius: 2,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Typography
                      fontWeight={600}
                      color="error"
                      sx={{ fontSize: { xs: "0.9rem", md: "1rem" } }}
                    >
                      Currently Unavailable
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ({outOfStockItems.length}{" "}
                      {outOfStockItems.length > 1 ? "items" : "item"})
                    </Typography>
                  </Box>
                  {outOfStockItems.map((item) => (
                    <CartItemRow key={item.id} item={item} />
                  ))}
                </Paper>
              )}

              {/* In-Stock Items Grouped by Delivery Time */}
              {sortedDeliveryTimes.map((deliveryTime) => {
                const items = groupedCartItems[deliveryTime];
                const estimatedDate = estimatedDateMap[items[0]?.productId];
                return (
                  <Paper
                    key={deliveryTime}
                    elevation={0}
                    sx={{
                      mb: 2,
                      p: 2,
                      bgcolor: "#fafafa",
                      border: "1px solid #e0e0e0",
                      borderRadius: 2,
                    }}
                  >
                    {!store.commonStore.isDirectDelivery && (
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <Typography
                          fontWeight={600}
                          color="success.main"
                          sx={{ fontSize: { xs: "0.9rem", md: "1rem" } }}
                        >
                          {getDeliveryDateText(deliveryTime, estimatedDate)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          ({items.length} {items.length > 1 ? "items" : "item"})
                        </Typography>
                      </Box>
                    )}
                    {items.map((item) => (
                      <CartItemRow key={item.id} item={item} />
                    ))}
                  </Paper>
                );
              })}
            </>
          )}
        </Box>
      </Box>

      {/* RIGHT SIDE – Summary */}
      <Box
        flex={{ xs: 1, md: 3 }}
        display="flex"
        flexDirection="column"
        gap={2}
        width={{ xs: "100%", md: "auto" }}
      >
        {/* Outlet Card */}
        <Box
          p={2}
          bgcolor="#fff"
          borderRadius={2}
          boxShadow="0 1px 4px rgba(0, 0, 0, 0.05)"
        >
          <Typography
            fontWeight={600}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <LocationPinIcon sx={{ fontSize: 14 }} />
            Outlet Details
          </Typography>
          {loader ? (
            <Skeleton variant="text" width={150} />
          ) : (
            <>
              <Typography variant="subtitle2" fontWeight={600}>
                {customer?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {customer?.shippingAddressLine1},{" "}
                {customer?.shippingAddressLine2}, {customer?.shippingCity?.name}
                , {customer?.shippingPincode}, {customer?.shippingState?.name}
              </Typography>
            </>
          )}
        </Box>

        {/* Quick Delivery Info */}
        {isQuickDelivery && cartItemCount > 0 && (
          <Box
            p={2}
            bgcolor="#fff"
            borderRadius={2}
            boxShadow="0 1px 4px rgba(0, 0, 0, 0.05)"
          >
            <Typography
              fontWeight={600}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <FlashOnIcon sx={{ color: "warning.main", fontSize: 16 }} /> Quick
              Delivery
            </Typography>
            <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
              Your order will be delivered as soon as possible.
            </Typography>
          </Box>
        )}

        {/* Coupon Card */}
        <Box
          p={2}
          bgcolor="#fff"
          borderRadius={2}
          boxShadow="0 1px 4px rgba(0, 0, 0, 0.05)"
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              fontWeight={600}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <LocalOfferIcon sx={{ fontSize: 14 }} /> Coupons
            </Typography>
            <Button
              size="small"
              sx={{ fontSize: 13, textTransform: "none", fontWeight: 500 }}
              onClick={() => setCouponOpen(true)}
            >
              View All
            </Button>
          </Box>
        </Box>

        <CouponPopup
          open={couponOpen}
          onClose={() => setCouponOpen(false)}
          coupons={couponStore.applicableCoupons}
          onApply={() => {
            setCouponOpen(false);
          }}
          appliedCoupon={null}
          onCouponUpdate={() => {}}
          loadingCoupons={couponStore.loadingApplicableCoupons}
          deliveryType={deliveryType}
          readOnly={true}
        />

        {/* Total Amount */}
        <Box
          p={2}
          bgcolor="#fff"
          borderRadius={2}
          boxShadow="0 1px 4px rgba(0, 0, 0, 0.05)"
          display="flex"
          flexDirection="column"
          gap={1.5}
        >
          <Typography
            fontWeight={600}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <PaymentsIcon sx={{ fontSize: 14 }} /> Total Amount
          </Typography>
          {loader ? (
            <Skeleton variant="text" />
          ) : (
            <>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Sub Total (excl.tax)
                </Typography>
                <Typography variant="body2">
                  ₹{(totals?.totalMRPAmount ?? 0).toFixed(2)}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Tax
                </Typography>
                <Typography variant="body2">
                  ₹{(totals?.totalTaxAmount ?? 0).toFixed(2)}
                </Typography>
              </Box>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="body2" color="text.secondary">
                  Delivery Charges
                </Typography>
                {(totals?.deliveryCharges ?? 0) === 0 ? (
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{
                      color: "#2e7d32",
                      bgcolor: "#e8f5e9",
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      fontSize: "0.78rem",
                      letterSpacing: 0.5,
                    }}
                  >
                    FREE
                  </Typography>
                ) : (
                  <Typography variant="body2">
                    ₹{(totals?.deliveryCharges ?? 0).toFixed(2)}
                  </Typography>
                )}
              </Box>

              {/* Free delivery progress message */}
              {(totals?.deliveryCharges ?? 0) === 0 ? (
                <Box
                  display="flex"
                  alignItems="center"
                  gap={0.75}
                  px={1.5}
                  py={1}
                  bgcolor="#e8f5e9"
                  borderRadius={1}
                  border="1px solid #a5d6a7"
                >
                  <Typography variant="body2" fontWeight={600} color="#2e7d32">
                    FREE Delivery Applied
                  </Typography>
                </Box>
              ) : (totals?.requiredAmountForFreeDelivery ?? 0) > 0 ? (
                <Box
                  px={1.5}
                  py={1}
                  bgcolor="#fff8e1"
                  borderRadius={1}
                  border="1px solid #ffe082"
                >
                  <Typography variant="body2" color="#e65100" fontWeight={500}>
                    Add items worth{" "}
                    <strong>
                      ₹{(totals?.requiredAmountForFreeDelivery ?? 0).toFixed(0)}
                    </strong>{" "}
                    more to get <strong>FREE Delivery</strong>
                  </Typography>
                </Box>
              ) : null}
              {(totals?.smallCartCharges ?? 0) > 0 && (
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Small Cart Charges
                  </Typography>
                  <Typography variant="body2">
                    ₹{(totals?.smallCartCharges ?? 0).toFixed(2)}
                  </Typography>
                </Box>
              )}
              {(totals?.couponDiscountAmount ?? 0) > 0 && (
                <Box display="flex" justifyContent="space-between">
                  <Typography
                    variant="body2"
                    color="success.main"
                    fontWeight={600}
                  >
                    Coupon Discount
                  </Typography>
                  <Typography
                    variant="body2"
                    color="success.main"
                    fontWeight={600}
                  >
                    -₹{(totals?.couponDiscountAmount ?? 0).toFixed(2)}
                  </Typography>
                </Box>
              )}
              <Divider />
              <Box
                display="flex"
                justifyContent="space-between"
                fontWeight={600}
              >
                <Typography fontWeight={700}>Total Amount</Typography>
                <Typography fontWeight={700}>
                  ₹{(totals?.finalCartTotalAmount ?? 0).toFixed(2)}
                </Typography>
              </Box>
            </>
          )}
          <Button
            variant="contained"
            fullWidth
            sx={{
              mt: 1,
              bgcolor: "success.main",
              fontWeight: 600,
              py: 1.2,
              borderRadius: 2,
            }}
            onClick={handleCheckout}
            disabled={!canCheckout || loader || inStockItems?.length === 0}
          >
            Proceed to Checkout →
          </Button>
        </Box>
      </Box>
    </Box>
  );
});

export default CartDetail;
